import type { CostDay } from "@/lib/api/types";
import { niceMax } from "@/lib/ai-history";
import { formatCount, formatEuro } from "@/lib/format";

const W = 720;
const H = 200;
const LEFT = 44;
const RIGHT = 8;
const TOP = 10;
const BOTTOM = 26;

function dayNumber(iso: string): number {
  return Number(iso.slice(8, 10));
}

function dayLabel(iso: string): string {
  return `${iso.slice(8, 10)}.${iso.slice(5, 7)}.`;
}

/** Abrufe je Tag, bezahlt und aus dem Cache. Die Zahlen gibt es zusätzlich als Tabelle. */
export function DaysChart({ days }: { days: CostDay[] }) {
  const total = (d: CostDay) => d.paid + d.cache;
  const max = niceMax(Math.max(0, ...days.map(total)));
  const inner = W - LEFT - RIGHT;
  const height = H - TOP - BOTTOM;
  const slot = days.length > 0 ? inner / days.length : inner;
  const barWidth = Math.max(2, slot * 0.7);
  const y = (value: number) => TOP + height - (height * value) / max;
  const withData = days.filter((d) => total(d) > 0);

  return (
    <section aria-labelledby="days-title" className="rounded-lg border border-line bg-white">
      <div className="flex flex-col gap-0.5 border-b border-[#e6e9e7] px-5 py-4">
        <h2 id="days-title" className="text-base font-semibold">
          Abrufe je Tag
        </h2>
        <span className="text-xs text-muted">Alle API-Aufrufe, bezahlt und aus dem Cache (Zeitzone Europe/Berlin).</span>
      </div>
      {withData.length === 0 ? (
        <p className="px-5 py-6 text-sm text-muted">In diesem Monat gab es keine Abrufe.</p>
      ) : (
        <div className="flex flex-col gap-3 px-5 py-4">
          <ul aria-label="Legende" className="flex flex-wrap gap-x-5 gap-y-1 text-[13px]">
            <li className="flex items-center gap-2">
              <span aria-hidden="true" className="inline-block size-3 rounded-sm bg-accent" />
              Bezahlte Abrufe
            </li>
            <li className="flex items-center gap-2">
              <span aria-hidden="true" className="inline-block size-3 rounded-sm border border-accent bg-[#dbe6f4]" />
              Cache-Treffer
            </li>
          </ul>
          <svg
            viewBox={`0 0 ${W} ${H}`}
            role="img"
            aria-label={`Abrufe je Tag, ${formatCount(days.reduce((sum, d) => sum + total(d), 0))} insgesamt`}
            className="h-auto w-full max-w-full"
          >
            {[0, 1, 2, 3, 4].map((i) => {
              const value = (max * i) / 4;
              return (
                <g key={i}>
                  <line x1={LEFT} x2={W - RIGHT} y1={y(value)} y2={y(value)} stroke="#e6e9e7" />
                  <text x={LEFT - 6} y={y(value) + 4} textAnchor="end" fontSize="11" fill="#59616b" fontFamily="monospace">
                    {formatCount(value)}
                  </text>
                </g>
              );
            })}
            {days.map((day, index) => {
              const x = LEFT + slot * index + (slot - barWidth) / 2;
              return (
                <g key={day.day}>
                  <title>{`${dayLabel(day.day)}: ${formatCount(day.paid)} bezahlt, ${formatCount(day.cache)} aus dem Cache`}</title>
                  {day.paid > 0 && (
                    <rect x={x} y={y(day.paid)} width={barWidth} height={TOP + height - y(day.paid)} fill="#1f5fad" />
                  )}
                  {day.cache > 0 && (
                    <rect
                      x={x}
                      y={y(day.paid + day.cache)}
                      width={barWidth}
                      height={y(day.paid) - y(day.paid + day.cache)}
                      fill="#dbe6f4"
                      stroke="#1f5fad"
                      strokeWidth="1"
                    />
                  )}
                  {(index === 0 || dayNumber(day.day) % 5 === 0) && (
                    <text x={x + barWidth / 2} y={H - 8} textAnchor="middle" fontSize="11" fill="#59616b" fontFamily="monospace">
                      {dayNumber(day.day)}.
                    </text>
                  )}
                </g>
              );
            })}
          </svg>
          <details>
            <summary className="inline-flex min-h-11 cursor-pointer items-center text-[13px] text-accent underline">
              Zahlen als Tabelle ({formatCount(withData.length)} Tage mit Abrufen)
            </summary>
            <div className="overflow-x-auto">
              <table aria-label="Abrufe je Tag" className="mt-2 w-full min-w-[420px] border-collapse text-sm">
                <thead>
                  <tr className="text-left text-xs text-muted">
                    <th className="px-3 py-2 font-medium">Tag</th>
                    <th className="px-3 py-2 text-right font-medium">Bezahlt</th>
                    <th className="px-3 py-2 text-right font-medium">Cache</th>
                    <th className="px-3 py-2 text-right font-medium">Euro-Kosten</th>
                  </tr>
                </thead>
                <tbody>
                  {withData.map((day) => (
                    <tr key={day.day} className="border-t border-neutral-bg">
                      <th scope="row" className="px-3 py-1.5 text-left font-mono text-[13px] font-normal">
                        {dayLabel(day.day)}
                      </th>
                      <td className="px-3 py-1.5 text-right font-mono text-[13px]">{formatCount(day.paid)}</td>
                      <td className="px-3 py-1.5 text-right font-mono text-[13px]">{formatCount(day.cache)}</td>
                      <td className="px-3 py-1.5 text-right font-mono text-[13px]">
                        {day.cost_eur > 0 ? formatEuro(day.cost_eur) : "–"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </details>
        </div>
      )}
    </section>
  );
}
